import React, { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Sparkles, Star, ThumbsUp, ThumbsDown, ListPlus, FileDown, Link2, Check, Crosshair } from "lucide-react";
import RankedList from "@/components/RankedList";
import BuildBriefDialog from "@/components/BuildBriefDialog";

function buildMarkdown(scan) {
  const r = scan.report || {};
  const sec = (title, items) =>
    `\n## ${title}\n\n` +
    (items || []).map((it, i) => {
      const q = (it.quotes || []).map((x) => `\n   > "${x}"`).join("");
      return `${i + 1}. **${it.theme}** — ${it.percentage}% (${it.mentions} mentions)\n   ${it.summary}${q}`;
    }).join("\n");
  return (
    `# Play Store Insight Report: ${scan.category}\n\n` +
    `Apps analyzed: ${scan.app_count} · Reviews analyzed: ${scan.total_reviews_analyzed}\n\n` +
    `> ${r.overall_summary || ""}\n` +
    sec("Feature Requests (most wanted)", r.feature_requests) +
    sec("What people like", r.likes) +
    sec("What people dislike", r.dislikes)
  );
}

const pct = (mentions, total) => (total ? Math.round((mentions / total) * 100) : 0);

function CompetitorLens({ apps }) {
  const candidates = apps.filter((a) => a.analysis);
  const [sel, setSel] = useState(candidates[0]?.app_id || "");
  if (candidates.length === 0) return null;
  const app = candidates.find((a) => a.app_id === sel) || candidates[0];
  const total = app.reviews_analyzed || 0;

  const lists = [
    { key: "likes", label: "Winning at", icon: ThumbsUp, cls: "text-emerald-600", border: "border-emerald-200" },
    { key: "dislikes", label: "Losing at", icon: ThumbsDown, cls: "text-red-500", border: "border-red-200" },
    { key: "feature_requests", label: "Users still want", icon: ListPlus, cls: "text-[#002FA7]", border: "border-blue-200" },
  ];

  return (
    <div className="mt-12" data-testid="competitor-lens">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Crosshair className="h-4 w-4 text-[#002FA7]" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Competitor Lens</span>
        </div>
        <Select value={sel} onValueChange={setSel}>
          <SelectTrigger className="w-[260px] rounded-sm border-zinc-300 h-10" data-testid="competitor-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {candidates.map((a) => (
              <SelectItem key={a.app_id} value={a.app_id}>{a.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <p className="text-sm text-zinc-500 mb-4 font-mono">Where <span className="text-zinc-900 font-medium">{app.title}</span> stands out or falls short across its {total} reviews.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
        {lists.map(({ key, label, icon: Icon, cls }) => {
          const items = (app.analysis[key] || [])
            .map((it) => ({ ...it, p: pct(it.mentions, total) }))
            .sort((a, b) => b.p - a.p)
            .slice(0, 5);
          return (
            <div key={key} className="bg-white p-5" data-testid={`competitor-col-${key}`}>
              <div className={`flex items-center gap-2 mb-3 ${cls}`}>
                <Icon className="h-4 w-4" />
                <span className="text-xs font-bold uppercase tracking-wider">{label}</span>
              </div>
              {items.length === 0 && <div className="text-xs text-zinc-400 font-mono">—</div>}
              <ul className="space-y-2.5">
                {items.map((it, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="font-mono text-xs text-zinc-400 mt-0.5 w-8 flex-shrink-0">{it.p}%</span>
                    <span className="text-sm text-zinc-800 leading-snug">{it.theme}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ReportView({ scan }) {
  const r = scan.report || {};
  const apps = scan.apps || [];
  const [copied, setCopied] = useState(false);

  const exportReport = () => {
    const blob = new Blob([buildMarkdown(scan)], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `insight-${scan.category.replace(/\s+/g, "-")}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyLink = async () => {
    const link = `${window.location.origin}/r/${scan.id}`;
    try { await navigator.clipboard.writeText(link); } catch (e) { /* noop */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="fade-up print-region" data-testid="report-view">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-1">Insight Report</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight capitalize" data-testid="report-category">
            {scan.category}
          </h1>
          <div className="flex gap-4 mt-2 font-mono text-xs text-zinc-500">
            <span>{scan.app_count} apps</span>
            <span className="text-zinc-300">/</span>
            <span data-testid="report-review-count">{scan.total_reviews_analyzed} reviews analyzed</span>
          </div>
        </div>
        <div className="flex gap-2 no-print">
          <BuildBriefDialog scanId={scan.id} />
          <Button onClick={copyLink} variant="outline" className="rounded-sm border-zinc-300 font-medium" data-testid="share-button">
            {copied ? <Check className="h-4 w-4 mr-2 text-emerald-600" /> : <Link2 className="h-4 w-4 mr-2" />}
            {copied ? "Copied!" : "Share"}
          </Button>
          <Button onClick={() => window.print()} variant="outline" className="rounded-sm border-zinc-300 font-medium" data-testid="pdf-button">
            <FileDown className="h-4 w-4 mr-2" /> PDF
          </Button>
          <Button onClick={exportReport} variant="outline" className="rounded-sm border-zinc-300 font-medium" data-testid="export-button">
            <Download className="h-4 w-4 mr-2" /> .md
          </Button>
        </div>
      </div>

      {/* Overall summary */}
      <div className="border border-zinc-200 bg-zinc-50 p-6 mb-8 flex gap-4" data-testid="report-summary">
        <Sparkles className="h-5 w-5 text-[#002FA7] flex-shrink-0 mt-0.5" />
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-2">The Opportunity</div>
          <p className="text-zinc-800 leading-relaxed">{r.overall_summary}</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="requests" className="w-full">
        <TabsList className="bg-zinc-100 p-1 rounded-sm h-auto flex-wrap justify-start no-print">
          <TabsTrigger value="requests" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2 px-4 py-2" data-testid="tab-feature-requests">
            <ListPlus className="h-4 w-4" /> Feature Requests
            <span className="font-mono text-xs text-zinc-400">{(r.feature_requests || []).length}</span>
          </TabsTrigger>
          <TabsTrigger value="likes" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2 px-4 py-2" data-testid="tab-likes">
            <ThumbsUp className="h-4 w-4" /> Things they like
            <span className="font-mono text-xs text-zinc-400">{(r.likes || []).length}</span>
          </TabsTrigger>
          <TabsTrigger value="dislikes" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2 px-4 py-2" data-testid="tab-dislikes">
            <ThumbsDown className="h-4 w-4" /> Things they dislike
            <span className="font-mono text-xs text-zinc-400">{(r.dislikes || []).length}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="mt-5">
          <p className="text-sm text-zinc-500 mb-3 font-mono">Ranked by share of reviewers requesting it — build the top one first. Click a row for real quotes.</p>
          <RankedList items={r.feature_requests} type="requests" />
        </TabsContent>
        <TabsContent value="likes" className="mt-5">
          <RankedList items={r.likes} type="likes" />
        </TabsContent>
        <TabsContent value="dislikes" className="mt-5">
          <RankedList items={r.dislikes} type="dislikes" />
        </TabsContent>
      </Tabs>

      {/* Competitor Lens */}
      <CompetitorLens apps={apps} />

      {/* Per-app breakdown */}
      <div className="mt-12">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-4">Apps Scanned</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
          {apps.map((a, i) => (
            <div key={i} className="bg-white p-5" data-testid={`app-card-${i}`}>
              <div className="flex items-center gap-3 mb-3">
                {a.icon ? (
                  <img src={a.icon} alt="" className="h-10 w-10 rounded-md flex-shrink-0" />
                ) : (
                  <div className="h-10 w-10 rounded-md bg-zinc-100 flex-shrink-0" />
                )}
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{a.title}</div>
                  <div className="flex items-center gap-2 font-mono text-xs text-zinc-400">
                    {a.score != null && (
                      <span className="flex items-center gap-0.5"><Star className="h-3 w-3 fill-zinc-400 text-zinc-400" />{Number(a.score).toFixed(1)}</span>
                    )}
                    <span>{a.reviews_analyzed} reviews</span>
                  </div>
                </div>
              </div>
              <dl className="text-xs space-y-1.5">
                {a.top_request && <div className="flex gap-2"><dt className="text-[#002FA7] font-medium w-16 flex-shrink-0">Wants</dt><dd className="text-zinc-600">{a.top_request}</dd></div>}
                {a.top_like && <div className="flex gap-2"><dt className="text-emerald-600 font-medium w-16 flex-shrink-0">Likes</dt><dd className="text-zinc-600">{a.top_like}</dd></div>}
                {a.top_dislike && <div className="flex gap-2"><dt className="text-red-500 font-medium w-16 flex-shrink-0">Dislikes</dt><dd className="text-zinc-600">{a.top_dislike}</dd></div>}
              </dl>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
